<template>
    <div class="card h-100 ui06b-classifiche">
        <div class="card-header">
            <h3 class="card-title mb-0">Classifiche</h3>
        </div>
        <div class="card-body">
            <div v-if="loading" class="placeholder-glow text-center py-4">
                <span class="placeholder col-8"></span>
            </div>

            <BlockState v-else-if="nonDisponibile" stato="non_disponibile"
                        dettaglio="Le classifiche non sono disponibili per questa richiesta." />

            <ul v-else class="nav nav-tabs mb-3" role="tablist">
                <li v-for="t in tab" :key="t.key" class="nav-item" role="presentation">
                    <button class="nav-link ui06b-tab" :class="{ active: tabAttivo === t.key }"
                            type="button" role="tab" :aria-selected="tabAttivo === t.key ? 'true' : 'false'"
                            @click="tabAttivo = t.key">{{ t.label }}</button>
                </li>
            </ul>

            <p v-if="elencoVuoto" class="text-muted text-center mb-0">Nessun dato nel periodo selezionato.</p>

            <div v-else class="table-responsive">
                <table class="table table-sm font-size-sm mb-0 align-middle ui06b-tabella">
                    <caption class="visually-hidden">Classifica: {{ tabLabel }}</caption>
                    <thead>
                        <tr>
                            <th scope="col">#</th>
                            <th scope="col">{{ etichettaPrincipale }}</th>
                            <!-- UI-06A: nella tab Progetti la terza colonna e' il CLIENTE
                                 (il progetto e' gia' identificato dal nome); nelle altre
                                 tab e' il conteggio progetti. Stessa struttura tabella. -->
                            <th v-if="tabAttivo === 'progetti'" scope="col">Cliente</th>
                            <th v-else scope="col" class="text-center">Progetti</th>
                            <th scope="col" class="text-end">Ricavo</th>
                            <th scope="col" class="text-end">Margine</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(r, i) in elencoLimitato" :key="r.chiave">
                            <td class="text-muted"><span class="ui06b-pos" :class="'ui06b-pos--' + classePosizione(i)">{{ i + 1 }}</span></td>
                            <th scope="row" class="fw-normal ui06b-nome" :title="r.principale">{{ r.principale }}</th>
                            <td v-if="tabAttivo === 'progetti'">{{ r.cliente || "—" }}</td>
                            <td v-else class="text-center">{{ r.progetti }}</td>
                            <td class="text-end ui06b-ricavo">{{ eur(r.fatturato) }}</td>
                            <td class="text-end ui06b-margine" :class="'ui06b-margine--' + segnoMargine(r.margine)">{{ eur(r.margine) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import BlockState from "./BlockState.vue";

const FORMAT_EUR = new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0,
});

const TAB = [
    { key: "account", label: "Account", principale: "Account" },
    { key: "clienti", label: "Clienti", principale: "Cliente" },
    { key: "progetti", label: "Progetti", principale: "Progetto" },
    { key: "tipologie", label: "Tipologie", principale: "Tipologia" },
];

export default {
    name: "RankingsPanel",
    components: { BlockState },
    props: {
        rankings: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        maxRighe: { type: Number, default: 0 }, // 0 = nessun limite (default), 10 = max 10 righe
    },

    data() {
        return { tabAttivo: "account" };
    },

    computed: {
        /* Limita le righe a maxRighe (default 10 in Dashboard Manager). */
        elencoLimitato() {
            if (this.maxRighe > 0) return this.elenco.slice(0, this.maxRighe);
            return this.elenco;
        },
        tab() { return TAB; },
        nonDisponibile() {
            return !this.loading && (this.rankings === null || this.rankings === undefined);
        },
        tabLabel() { return (TAB.find((t) => t.key === this.tabAttivo) || TAB[0]).principale; },
        etichettaPrincipale() { return this.tabLabel; },
        grezzo() {
            const r = this.rankings && this.rankings[this.tabAttivo];
            return Array.isArray(r) ? r : [];
        },
        elenco() {
            return this.grezzo.map((r, i) => ({
                chiave: this.tabAttivo + "-" + i,
                principale: this.denominazione(r) || "—",
                progetti: r.progetti,
                cliente: r.cliente,
                fatturato: r.fatturato,
                margine: r.margine,
            }));
        },
        elencoVuoto() { return this.elenco.length === 0; },
    },

    methods: {
        eur(v) { return FORMAT_EUR.format(typeof v === "number" ? v : 0); },

        /* UI-06B: due helper SOLO presentazionali.
           Non ricalcolano, non riordinano, non filtrano: il numero mostrato e'
           esattamente quello di prima, cambia solo l'aspetto.

           - classePosizione: il badge dei primi 3 posti. Dal 4° in poi la
             posizione resta testo normale (.ui06b-pos--zero), senza badge.
             L'indice i non viene toccato: e' la posizione mostrata come prima. */
        classePosizione(i) {
            return i < 3 ? String(i + 1) : "zero";
        },

        /* UI-06B: il colore del Margine dipende SOLO dal segno del valore gia'
           presente nel payload. Formato, segno e valore restano quelli di eur():
           niente '+', niente arrotondamenti, nessun ricalcolo. Un valore non
           numerico resta neutro, coerente con eur() che lo tratta come 0. */
        segnoMargine(v) {
            if (typeof v !== "number") return "zero";
            if (v > 0) return "pos";
            if (v < 0) return "neg";
            return "zero";
        },

        /* Ogni dimensione ha il proprio campo etichetta nel contract.
           UI-06A: per i PROGETTI si mostra il NOME (label), non "codice — nome":
           il codice resta disponibile nel campo code senza sostituire il nome. */
        denominazione(r) {
            return r.label;
        },
    },
}
</script>

<style scoped>
/* UI-06B - POLISH VISUALE della card Classifiche.
   FASE ESCLUSIVAMENTE GRAFICA: qui non c'e' logica, nessun dato, nessun
   ordinamento, nessun limite di righe. Cambia solo l'aspetto.

   Tutte le regole sono `scoped` E ancorate a .ui06b-classifiche: serve a
   superare le regole di Bootstrap/tema (.nav-tabs .nav-link.active,
   .table>:not(caption)>*>*) senza usare !important e senza uscire da
   questo componente.

   Palette ripresa da ProjectStatusCard (UI-02B), gia' presente in dashboard:
   grigi #1f2a37 / #475569 / #64748b / #94a3b8, bordi #e9edf2 / #f1f5f9,
   blu #0a58ca, verde #168a4c, ambra #b45309. */

/* ---------- 2. TAB ATTIVA ----------
   Grammatica IDENTICA per Account, Clienti, Progetti e Tipologie: nessuna
   tab ha un colore proprio. Solo lo stato attivo e' colorato. */
.ui06b-classifiche .nav-tabs .ui06b-tab {
    color: #64748b;
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    font-weight: 500;
    transition: color .15s ease, background-color .15s ease, border-color .15s ease;
}

/* Le tab NON attive restano neutre: al passaggio del mouse solo una sfumatura
   grigia, mai un colore di sezione. */
.ui06b-classifiche .nav-tabs .ui06b-tab:not(.active):hover {
    color: #475569;
    background-color: #f7f9fc;
}

.ui06b-classifiche .nav-tabs .ui06b-tab.active {
    color: #0a58ca;
    background-color: #f1f7ff;
    border-color: #cfe5ff;
    font-weight: 600;
}

/* ---------- 3. HEADER DELLA TABELLA ----------
   Fondo tenuissimo blu/grigio + un solo bordo inferiore: separa le
   intestazioni dai dati senza creare una fascia pesante. Colonne,
   allineamenti e ordine delle colonne NON cambiano. */
.ui06b-classifiche .ui06b-tabella thead th {
    background-color: #f3f7fb;
    color: #475569;
    border-bottom: 1px solid #e9edf2;
    white-space: nowrap;
}

/* ---------- 4. BADGE DELLE POSIZIONI ----------
   Solo il NUMERO delle prime 3 righe. La riga intera resta neutra.
   1 = blu, 2 = verde/teal, 3 = ambra tenue. Nessuna emoji/medaglia. */
.ui06b-classifiche .ui06b-pos {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.55rem;
    height: 1.55rem;
    padding: 0 .3rem;
    border-radius: 7px;
    font-size: .72rem;
    font-weight: 700;
    line-height: 1;
}

.ui06b-classifiche .ui06b-pos--1 { background-color: #e3efff; color: #0a58ca; }
.ui06b-classifiche .ui06b-pos--2 { background-color: #e3f7ec; color: #168a4c; }
.ui06b-classifiche .ui06b-pos--3 { background-color: #fdf1df; color: #b45309; }

/* Dal 4° posto: numero normale, senza badge e senza sfondo. */
.ui06b-classifiche .ui06b-pos--zero {
    min-width: 0;
    background-color: transparent;
    color: #94a3b8;
    font-weight: 500;
}

/* ---------- 5. COLONNA RICAVO ----------
   Accento leggero: colore + peso. Nessun badge, nessun background. */
.ui06b-classifiche .ui06b-ricavo {
    color: #0a58ca;
    font-weight: 600;
}

/* ---------- 6. COLONNA MARGINE ----------
   Il colore e' un'informazione SEMANTICA sul segno del valore gia' presente
   nel payload. Il testo, il formato e il segno restano quelli di eur(). */
.ui06b-classifiche .ui06b-margine { font-weight: 600; }
.ui06b-classifiche .ui06b-margine--pos  { color: #168a4c; }
.ui06b-classifiche .ui06b-margine--neg  { color: #b91c1c; }
.ui06b-classifiche .ui06b-margine--zero { color: #64748b; }

/* ---------- 7. HOVER DELLE RIGHE ----------
   Azzurro chiarissimo, appena percettibile. Si agisce sul SOLO sfondo:
   `table-hover` di Bootstrap 5.3 sovrascriverebbe anche `color` e annullerebbe
   i colori semantici di Ricavo e Margine, quindi qui non viene usato.

   La riga NON e' cliccabile in questa fase: nessun `cursor: pointer`, nessun
   handler, nessun ruolo interattivo aggiunto. Il cursore resta com'e'. */
.ui06b-classifiche .ui06b-tabella > tbody > tr > * {
    transition: background-color .15s ease;
}

.ui06b-classifiche .ui06b-tabella > tbody > tr:hover > * {
    background-color: #f5f9ff;
}

/* ---------- 9. NOMI LUNGHI ----------
   La tab Progetti mostra nomi molto lunghi (es. "VENUS- Opera d'arte ...").
   Il testo continua a andare a capo come prima: qui si impedisce solo che un
   token unico (URL, codice) spacchi la tabella. Nessuna larghezza rigida. */
.ui06b-classifiche .ui06b-nome {
    font-weight: 500;
    overflow-wrap: anywhere;
}

/* Rispetta la preferenza del sistema operativo. */
@media (prefers-reduced-motion: reduce) {
    .ui06b-classifiche .nav-tabs .ui06b-tab,
    .ui06b-classifiche .ui06b-tabella > tbody > tr > * {
        transition: none;
    }
}

/* Altezza fissa per la card Classifiche: usa l'altezza del tab Tipologie come riferimento.
   La card non deve espandersi o contrarsi al cambio tab. */
.ui06b-classifiche {
    --ui06b-h: 420px; /* Altezza riferimento tab Tipologie (max 10 righe) */
}
.ui06b-classifiche .card-body {
    height: var(--ui06b-h);
    display: flex;
    flex-direction: column;
}
.ui06b-classifiche .nav-tabs {
    flex-shrink: 0;
}
.ui06b-classifiche .tab-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}
.ui06b-classifiche .table-responsive {
    height: 100%;
    overflow-y: auto;
}
.ui06b-classifiche .ui06b-tabella {
    margin-bottom: 0;
}

/* Scrollbar sottile per la tabella */
.ui06b-classifiche .table-responsive::-webkit-scrollbar {
    width: 6px;
}
.ui06b-classifiche .table-responsive::-webkit-scrollbar-track {
    background: transparent;
}
.ui06b-classifiche .table-responsive::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}
</style>
